import React, { useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Activity, AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { ACCOUNT_SIGNALS } from '@/graphql/account-signals-queries'
import { ButtonProps } from '@/types'

// Type cast for the Button component from JSX file
const Button = ButtonComponent as React.FC<ButtonProps>

interface SignalEvent {
    event: string
    count: number
    last_seen?: string | null
}

interface SignalFinding {
    level: 'warning' | 'info' | 'ok' | string
    code: string
    message: string
}

interface DatasetQuality {
    event: string
    match_quality: number | null
    coverage: number | null
    upload_frequency: string | null
}

export interface AccountSignals {
    checked_at: string
    events_window_days: number
    pixel_id: string | null
    pixel_status: string
    pixel_name: string | null
    pixel_last_fired_time: string | null
    pixel_shared_with_ad_account: string
    events_status: string
    events: SignalEvent[]
    conversions_api_status: string
    conversions_api_events: SignalEvent[]
    custom_audience_terms: string
    terms_accepted: string[]
    dataset_quality_status: string
    dataset_quality: DatasetQuality[]
    findings: SignalFinding[]
}

export const SIGNALS_NOT_AVAILABLE = 'Signals are not available right now.'
// Meta is read again at most once a minute; the server returns the last read within that time
export const CHECK_AGAIN_COOLDOWN_MS = 60 * 1000
export const NOT_AVAILABLE = 'Not available'
export const UNKNOWN = 'Unknown'

const PIXEL_STATUS_TEXT: Record<string, string> = {
    connected: 'Connected',
    not_connected: 'No pixel connected',
    unavailable: 'Unavailable on Meta',
    unknown: UNKNOWN,
}

const SHARED_TEXT: Record<string, string> = { yes: 'Yes', no: 'No', unknown: UNKNOWN }

const TERMS_TEXT: Record<string, string> = {
    accepted: 'Accepted',
    not_reported: 'Not reported as accepted by Meta',
    unknown: UNKNOWN,
}

export const pixelText = (signals: AccountSignals): string => {
    const status = PIXEL_STATUS_TEXT[signals.pixel_status] ?? UNKNOWN
    return signals.pixel_status === 'connected' && signals.pixel_name ? `${signals.pixel_name} (${status})` : status
}

export const conversionsApiText = (signals: AccountSignals): string => {
    if (signals.conversions_api_status === 'receiving') {
        const total = signals.conversions_api_events.reduce((sum, event) => sum + event.count, 0)
        return `Receiving server events (${total} in ${signals.events_window_days} days)`
    }
    if (signals.conversions_api_status === 'none') return `No server events in ${signals.events_window_days} days`
    if (signals.conversions_api_status === 'not_applicable') return 'No pixel'
    return NOT_AVAILABLE
}

export const termsText = (signals: AccountSignals): string => TERMS_TEXT[signals.custom_audience_terms] ?? UNKNOWN

export const matchQualityText = (signals: AccountSignals): string => {
    if (signals.dataset_quality_status !== 'available') return NOT_AVAILABLE
    const scored = signals.dataset_quality.filter((item) => item.match_quality !== null)
    if (scored.length === 0) return NOT_AVAILABLE
    return scored.map((item) => `${item.event} ${item.match_quality}/10`).join(', ')
}

const FINDING_STYLES: Record<string, { icon: React.ElementType; className: string }> = {
    warning: { icon: AlertTriangle, className: 'tw:text-yellow-400' },
    info: { icon: Info, className: 'tw:text-gray-200' },
    ok: { icon: CheckCircle2, className: 'tw:text-green-400' },
}

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="tw:flex tw:justify-between tw:gap-4 tw:text-sm">
        <span className="tw:text-gray-200">{label}</span>
        <span className="tw:text-right">{value}</span>
    </div>
)

interface SignalPanelProps {
    publisherId?: string | null
    campaignId?: string | null
    objective?: string | null
}

/**
 * Read-only check of the pixel, Conversions API and ad account terms of the connected ad account, with
 * what they mean for the chosen objective. Nothing is read until the panel is opened.
 */
const SignalPanel: React.FC<SignalPanelProps> = ({ publisherId, campaignId, objective }) => {
    const [open, setOpen] = useState(false)
    const [coolingDown, setCoolingDown] = useState(false)
    const [loadSignals, { data, loading, error }] = useLazyQuery(ACCOUNT_SIGNALS, { fetchPolicy: 'network-only' })
    const canLoad = !!(publisherId && campaignId)

    useEffect(() => {
        if (open && canLoad) loadSignals({ variables: { publisherId, campaignId, objective } })
    }, [open, canLoad, publisherId, campaignId, objective, loadSignals])

    useEffect(() => {
        if (!coolingDown) return undefined
        const timer = setTimeout(() => setCoolingDown(false), CHECK_AGAIN_COOLDOWN_MS)
        return () => clearTimeout(timer)
    }, [coolingDown])

    const checkAgain = () => {
        if (coolingDown || loading) return
        setCoolingDown(true)
        loadSignals({ variables: { publisherId, campaignId, objective, refresh: true } })
    }

    if (!open) {
        return (
            <div className="tw:pt-6">
                <Button
                    variant="link"
                    className="tw:px-0 tw:gap-1"
                    onClick={() => {
                        setOpen(true)
                        setCoolingDown(true)
                    }}
                >
                    <Activity className="tw:w-4 tw:h-4" />
                    Check pixel and ad account signals
                </Button>
            </div>
        )
    }

    const signals: AccountSignals | undefined = data?.accountSignals

    return (
        <div
            className="tw:mt-6 tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:bg-gray-500 tw:px-4 tw:py-3"
            data-testid="signal-panel"
        >
            <div className="tw:flex tw:items-center tw:justify-between">
                <span className="tw:text-sm tw:font-semibold">Pixel and ad account signals</span>
                {signals && (
                    <Button variant="link" className="tw:px-0" disabled={coolingDown || loading} onClick={checkAgain}>
                        Check again
                    </Button>
                )}
            </div>

            {!canLoad || error ? (
                <span className="tw:text-sm tw:text-gray-200">{SIGNALS_NOT_AVAILABLE}</span>
            ) : loading || !signals ? (
                <span className="tw:text-sm tw:text-gray-200">Checking…</span>
            ) : (
                <>
                    <ul className="tw:flex tw:flex-col tw:gap-1.5" aria-label="Signal assessment">
                        {signals.findings.map((finding) => {
                            const style = FINDING_STYLES[finding.level] ?? FINDING_STYLES.info
                            const Icon = style.icon
                            return (
                                <li
                                    key={finding.code}
                                    className="tw:flex tw:gap-2 tw:text-sm"
                                    data-level={finding.level}
                                >
                                    <Icon className={`tw:w-4 tw:h-4 tw:flex-none tw:mt-0.5 ${style.className}`} />
                                    <span>{finding.message}</span>
                                </li>
                            )
                        })}
                    </ul>

                    <div className="tw:flex tw:flex-col tw:gap-1">
                        <Row label="Pixel" value={pixelText(signals)} />
                        {signals.pixel_status !== 'not_connected' && (
                            <Row
                                label="Shared with the ad account"
                                value={SHARED_TEXT[signals.pixel_shared_with_ad_account] ?? UNKNOWN}
                            />
                        )}
                        <Row label="Conversions API" value={conversionsApiText(signals)} />
                        <Row label="Custom Audience terms" value={termsText(signals)} />
                        <Row label="Event match quality" value={matchQualityText(signals)} />
                    </div>

                    {signals.events_status === 'available' && (
                        <div className="tw:flex tw:flex-col tw:gap-1">
                            <span className="tw:text-sm tw:font-semibold">
                                Events in the last {signals.events_window_days} days
                            </span>
                            {signals.events.length === 0 ? (
                                <span className="tw:text-sm tw:text-gray-200">None</span>
                            ) : (
                                <ul aria-label="Pixel events">
                                    {signals.events.map((event) => (
                                        <li key={event.event} className="tw:flex tw:justify-between tw:text-sm">
                                            <span>{event.event}</span>
                                            <span>{event.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    {signals.events_status === 'unknown' && <Row label="Events" value={UNKNOWN} />}
                </>
            )}
        </div>
    )
}

export default SignalPanel
